import { forwardRef, useId, type ReactNode } from "react";
import { cn } from "~/lib/cn";

type CheckboxBaseProps = Omit<React.ComponentProps<"input">, "type">;

// Either `label` (visible content — may carry inline links, e.g. a consent
// label pointing at the privacy policy) or `aria-label` (icon-only) is required.
// Mirrors the IconA11yConstraint pattern from ui/Button.
type CheckboxProps = CheckboxBaseProps &
  {
    error?: string;
    /**
     * `public` (T872 S3) — the delivery's `.test-consent`: the native 20px box with the
     * brand accent beside a 14px line that may carry a link; `default` is the auth-form
     * drawn box (`.af-check`).
     */
    variant?: "default" | "public";
  } &
  (
    | { label: ReactNode; "aria-label"?: string }
    | { label?: undefined; "aria-label": string }
  );

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ label, error, className, id, disabled, variant = "default", ...props }, ref) {
    const generatedId = useId();
    const checkboxId =
      id ?? (typeof label === "string" ? label.toLowerCase().replace(/\s+/g, "-") : generatedId);
    const helpId = error ? `${checkboxId}-error` : undefined;

    if (variant === "public") {
      return (
        <div className="pub-consent-field">
          <label htmlFor={checkboxId} className={cn("pub-consent", error && "is-error", disabled && "is-disabled")}>
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              disabled={disabled}
              className={cn("pub-consent-input", className)}
              aria-invalid={error ? true : undefined}
              aria-describedby={helpId}
              {...props}
            />
            {label && <span className="pub-consent-label">{label}</span>}
          </label>
          {error && (
            <p id={helpId} className="pub-field-error" role="alert">
              {error}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="af-field">
        <label
          htmlFor={checkboxId}
          className={cn(
            "af-check",
            error && "is-error",
            disabled && "is-disabled",
          )}
        >
          <input
            ref={ref}
            id={checkboxId}
            type="checkbox"
            disabled={disabled}
            className={className}
            aria-invalid={error ? true : undefined}
            aria-describedby={helpId}
            {...props}
          />
          <span className="af-check-box">
            <svg
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="2 7 6 11 12 3" />
            </svg>
          </span>
          {label && <span className="af-check-label">{label}</span>}
        </label>
        {error && (
          <p id={helpId} className="af-helper is-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
